import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Icons, NamedLink } from '../../../components';
import { extractPlainTextFromContent } from '../../../util/genericHelpers';
import {
  getCommunityPosts,
  getCommunityVideos,
  getSocialLinks,
  getStrapiMediaUrl,
} from '../../../util/strapiApi';
import css from './SectionCommunityPreview.module.css';

const FALLBACK_SOCIALS = [
  { platform: 'instagram', url: 'https://www.instagram.com', label: 'Instagram' },
  { platform: 'facebook', url: 'https://www.facebook.com', label: 'Facebook' },
  { platform: 'youtube', url: 'https://www.youtube.com', label: 'YouTube' },
];

const FALLBACK_POSTS = {
  whatsHot: [
    { title: 'Top 5 tile trends for spring', body: [{ type: 'text', text: '' }] },
    { title: 'Weekend tournament highlights', body: [{ type: 'text', text: '' }] },
  ],
  featured: [
    { title: 'Collector spotlight: Vintage bamboo sets', body: [{ type: 'text', text: '' }] },
    { title: 'Member story: From casual to competitive', body: [{ type: 'text', text: '' }] },
  ],
  matchesStyle: [
    { title: 'Mix & match racks with pastel tiles', body: [{ type: 'text', text: '' }] },
    { title: 'Style board: Coastal mats + classic tiles', body: [{ type: 'text', text: '' }] },
  ],
  partner: [
    { title: 'Mahj Club Meetup Calendar', externalLink: '#' },
    { title: 'Beginner tutorials by Mahjong Hub', externalLink: '#' },
  ],
};

const FALLBACK_VIDEOS = [
  { title: 'Winning hands explained', thumbnail: null, videoUrl: '#' },
  { title: 'Table setup in 90 seconds', thumbnail: null, videoUrl: '#' },
];

const SOCIAL_ICON_MAP = {
  instagram: 'instagram',
  facebook: 'facebook',
  youtube: 'youtube',
  tiktok: 'link',
  pinterest: 'link',
  other: 'link',
};

const getPreviewText = post => {
  if (!post) return '';
  if (post.title) return post.title;
  if (post.body) return extractPlainTextFromContent(post.body);
  return '';
};

function SectionCommunityPreview() {
  const [socialLinks, setSocialLinks] = useState(FALLBACK_SOCIALS);
  const [whatsHotPosts, setWhatsHotPosts] = useState(FALLBACK_POSTS.whatsHot);
  const [featuredPosts, setFeaturedPosts] = useState(FALLBACK_POSTS.featured);
  const [matchesPosts, setMatchesPosts] = useState(FALLBACK_POSTS.matchesStyle);
  const [partnerPosts, setPartnerPosts] = useState(FALLBACK_POSTS.partner);
  const [videos, setVideos] = useState(FALLBACK_VIDEOS);

  useEffect(() => {
    let active = true;

    const loadCommunity = async () => {
      try {
        const [social, whatsHot, featured, matches, partners, videosData] = await Promise.all([
          getSocialLinks(),
          getCommunityPosts({ type: 'whatsHot', limit: 3 }),
          getCommunityPosts({ type: 'featured', limit: 3 }),
          getCommunityPosts({ type: 'matchesStyle', limit: 3 }),
          getCommunityPosts({ type: 'partner', limit: 3 }),
          getCommunityVideos({ featured: true, limit: 3 }),
        ]);

        if (!active) return;

        if (social?.length) setSocialLinks(social);
        if (whatsHot?.length) setWhatsHotPosts(whatsHot);
        if (featured?.length) setFeaturedPosts(featured);
        if (matches?.length) setMatchesPosts(matches);
        if (partners?.length) setPartnerPosts(partners);
        if (videosData?.length) setVideos(videosData);
      } catch (error) {
        // Fallbacks already set
      }
    };

    loadCommunity();

    return () => {
      active = false;
    };
  }, []);

  const videosWithThumbs = useMemo(() => {
    return videos.map(video => ({
      ...video,
      thumbnailUrl: getStrapiMediaUrl(video.thumbnail) || '',
    }));
  }, [videos]);

  return (
    <section className={css.communityPreviewSection}>
      <div className={css.fixedWidthContainer}>
        <div className={css.sectionHead}>
          <h2>Community</h2>
          <p>Social. Connect. Learn. Play.</p>
        </div>

        <div className={css.previewGrid}>
          <div className={css.previewCard}>
            <h3>Follow the MM Community</h3>
            <div className={css.socialLinks}>
              {socialLinks.map(link => {
                const iconName = SOCIAL_ICON_MAP[link.platform] || SOCIAL_ICON_MAP.other;
                return (
                  <ExternalLink key={link.url} href={link.url} className={css.socialLink}>
                    <span className={css.socialIcon}>
                      <Icons name={iconName} />
                    </span>
                    <span>{link.label || link.platform}</span>
                  </ExternalLink>
                );
              })}
            </div>
          </div>

          <div className={css.previewCard}>
            <h3>What’s Hot in Mahj</h3>
            <ul className={css.previewList}>
              {whatsHotPosts.map(post => (
                <li key={post.id || post.title}>{getPreviewText(post)}</li>
              ))}
            </ul>
          </div>

          <div className={css.previewCard}>
            <h3>Featured Posts</h3>
            <ul className={css.previewList}>
              {featuredPosts.map(post => (
                <li key={post.id || post.title}>{getPreviewText(post)}</li>
              ))}
            </ul>
          </div>

          <div className={css.previewCard}>
            <h3>Featured Mahj Market Videos</h3>
            <div className={css.videoList}>
              {videosWithThumbs.map(video => (
                <ExternalLink
                  key={video.id || video.title}
                  href={video.videoUrl || '#'}
                  className={css.videoItem}
                >
                  <div
                    className={css.videoThumb}
                    style={{
                      backgroundImage: video.thumbnailUrl ? `url(${video.thumbnailUrl})` : undefined,
                    }}
                  />
                  <span>{video.title}</span>
                </ExternalLink>
              ))}
            </div>
          </div>

          <div className={css.previewCard}>
            <h3>Matches & Style</h3>
            <ul className={css.previewList}>
              {matchesPosts.map(post => (
                <li key={post.id || post.title}>{getPreviewText(post)}</li>
              ))}
            </ul>
          </div>

          <div className={css.previewCard}>
            <h3>Partner Content</h3>
            <ul className={css.previewList}>
              {partnerPosts.map(post => (
                <li key={post.id || post.title}>
                  {post.externalLink ? (
                    <ExternalLink href={post.externalLink}>{post.title || 'Partner item'}</ExternalLink>
                  ) : (
                    post.title || 'Partner item'
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={css.communityAction}>
          <NamedLink name="CommunityPage" className={css.communityButton}>
            Community
          </NamedLink>
        </div>
      </div>
    </section>
  );
}

export default SectionCommunityPreview;
